import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Checkbox, FormControlLabel, Button, Stack, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  salesDocKindFromSlug, SALES_DOCUMENT_META,
} from '../config/salesDocumentLineColumns';
import { loadColumnVisibility, saveColumnVisibility } from '../components/LineTableColumnSettings';
import { BRAND } from '../theme/tokens';

export const SalesDocumentSettingsPage: React.FC = () => {
  const { docType } = useParams<{ docType: string }>();
  const [search] = useSearchParams();
  const kind = salesDocKindFromSlug(docType);
  const meta = kind ? SALES_DOCUMENT_META[kind] : null;
  const returnTo = search.get('returnTo') ?? '/';
  const columnIds = useMemo(() => meta?.columns.map(c => c.id) ?? [], [meta]);

  const [visible, setVisible] = useState<Record<string, boolean>>(() =>
    meta ? loadColumnVisibility(meta.docSettingsStorageKey, columnIds) : {});

  if (!meta || !kind) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Alert severity="warning">Unknown document type.</Alert>
      </Box>
    );
  }

  const toggle = (id: string) => {
    setVisible(prev => {
      const next = { ...prev, [id]: prev[id] === false };
      saveColumnVisibility(meta.docSettingsStorageKey, next);
      return next;
    });
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Button
        component={RouterLink}
        to={returnTo}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2, color: '#666', textTransform: 'none' }}
      >
        Back to {meta.labelShort}
      </Button>

      <Typography variant="h5" sx={{ fontWeight: 800, color: '#333', mb: 0.5 }}>
        Document Settings — {meta.label}
      </Typography>
      <Typography sx={{ fontSize: '0.85rem', color: '#666', mb: 2, lineHeight: 1.5 }}>
        Default columns shown while {meta.editPhrase}. These apply to new {meta.label}s and as
        the starting layout when you open a draft. You can still hide or show columns per document
        using the <strong>⚙</strong> cog on the line table. For printed/PDF layout, use{' '}
        <RouterLink to={`${meta.templateSettingsPath}?returnTo=${encodeURIComponent(returnTo)}`} style={{ color: BRAND }}>
          Template Settings
        </RouterLink>.
      </Typography>

      <Paper sx={{ p: 2.5, border: '1px solid #e8e8ec' }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
          Line table columns (while editing)
        </Typography>
        <Stack spacing={0.25}>
          {meta.columns.map(col => (
            <FormControlLabel
              key={col.id}
              disabled={col.locked}
              control={
                <Checkbox
                  size="small"
                  checked={col.locked || visible[col.id] !== false}
                  onChange={() => !col.locked && toggle(col.id)}
                  sx={{ '&.Mui-checked': { color: BRAND } }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{col.label}</Typography>
                  {col.hint && (
                    <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>{col.hint}</Typography>
                  )}
                </Box>
              }
            />
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

export default SalesDocumentSettingsPage;
